import { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Button } from "@etframework/ui.components";
import {
  useUpdateStage,
  useDeleteStage,
} from "../../data/mutations/stageMutations";
import { useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import { toast } from "react-toastify";
import { StageNameTextBox } from "../../components/StageComponents/StageModalComponents/StageNameTextbox";
import { StageColorPicker } from "../../components/StageComponents/StageModalComponents/StageColorPicker";
import { ValidationProvider, useValidation } from "@etframework/ui.components";

export const UpdateStageModal = () => {
  return (
    <ValidationProvider>
      <InnerUpdateStageModal />
    </ValidationProvider>
  );
};
const InnerUpdateStageModal = () => {
  const stageTitleRef = useRef();
  const stageColorRef = useRef();

  const { mutate: updateStage, isPending: isUpdatePending } = useUpdateStage();
  const { mutate: deleteStage, isPending: isDeletePending } = useDeleteStage();
  const { setValidationErrors, clearValidationErrors } = useValidation();
  const { t } = useTranslation(["Stages"], {
    useSuspense: false,
  });

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);
  const [stageData, setStagaData] = useState({});

  const onModelBeforeClose = () => {};

  const closeModal = () => {
    clearValidationErrors();
    setShowModal(false);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const toggleModal = (event) => {
    const stageData = event?.detail?.stage;
    if (stageData) setStagaData(stageData);

    if (showModal) {
      closeModal();
      return;
    } else {
      openModal();
    }
  };

  const ModalContent = () => {
    const onUpdateStage = () => {
      const titleValue = stageTitleRef.current.getValue();
      const colorValue = stageColorRef.current.getValue();

      const postData = {
        Id: stageData.Id,
        Title: titleValue,
        Color: colorValue,
        BoardId: stageData.BoardId,
      };

      updateStage(
        { stage: postData },
        {
          onSuccess: () => {
            closeModal();
            toast.success(t("UpdateSuccess"), { position: "bottom-left" });
          },
          onError: (errors) => {
            setValidationErrors(errors?.data);
            toast.error(t("ErrorMsg"), { position: "bottom-left" });
          },
        }
      );
    };

    const onDeleteStage = () => {
      deleteStage(
        { stage: stageData.Id },
        {
          onSuccess: () => {
            closeModal();
            toast.success(t("DeleteSuccess"), { position: "bottom-left" });
          },
          onError: (errors) => {
            setValidationErrors(errors?.data);
            toast.error(t("ErrorMsg"), { position: "bottom-left" });
          },
        }
      );
    };

    return (
      <div className="et-stage-modal">
        <div>
          <span className="stage-header d-block">{t("UpdateStage")}</span>
        </div>

        <div className="row">
          <div className="col-4">
            <StageColorPicker
              id={ModalEvents.UpdateStageModal.Id + "_color-picker-input"}
              ref={stageColorRef}
              colorLabel={t("UpdateStageColorLabel")}
              color={stageData?.Color}
              change={(e) => {
                setStagaData((prev) => ({ ...prev, Color: e.value }));
              }}
            />
          </div>
          <div className="col-8">
            <StageNameTextBox
              id={ModalEvents.UpdateStageModal.Id + "_name-input"}
              name="Title"
              ref={stageTitleRef}
              nameLabel={t("UpdateStageNameLabel")}
              stageName={stageData?.Title}
              change={(e) => {
                setStagaData((prev) => ({ ...prev, Title: e.value }));
                clearValidationErrors();
              }}
            />
          </div>
        </div>

        <div className="d-flex justify-content-between pt-4">
          {!stageData?.Locked ? (
            <div>
              <Button
                id={ModalEvents.UpdateStageModal.Id + "_delete-button"}
                text={t("Delete")}
                className="btn-tertiary"
                onClick={onDeleteStage}
                iconCss="fa-regular fa-trash-can"
                loading={isDeletePending}
              />
            </div>
          ) : (
            <div></div>
          )}

          <div className="d-flex justify-content-end gap-3">
            <Button
              text={t("Cancel")}
              className="btn-tertiary"
              onClick={() => modalRef.current.close()}
            />
            <Button
              id={ModalEvents.UpdateStageModal.Id + "_update-button"}
              text={t("AddStage")}
              className="btn btn-primary"
              onClick={onUpdateStage}
              loading={isUpdatePending}
            />
          </div>
        </div>
      </div>
    );
  };

  useRegisterEvent(ModalEvents.UpdateStageModal, toggleModal);

  return ReactDOM.createPortal(
    <Modal
      id={ModalEvents.UpdateStageModal.Id}
      ref={modalRef}
      isModal
      showCloseIcon={true}
      visible={showModal}
      content={<ModalContent />}
      close={closeModal}
      open={openModal}
      beforeClose={onModelBeforeClose}
      minHeight="20vh"
      width="500px"
    ></Modal>,
    document.body
  );
};
